import clsx from 'clsx';
import {CustomMenu} from '../../menus/custom-menu';
import {LocaleSwitcher} from '../../i18n/locale-switcher';
import {Button} from '../buttons/button';
import {DarkModeIcon} from '../../icons/material/DarkMode';
import {LightbulbIcon} from '../../icons/material/Lightbulb';
import {Trans} from '../../i18n/trans';
import {useThemeSelector} from '../themes/theme-selector-context';
import {useSettings} from '../../core/settings/use-settings';

interface Props {
  className?: string;
  padding?: string;
}

export function Footer({className, padding}: Props) {
  const {branding} = useSettings();
  return (
    <footer
      className={clsx(
        'text-sm border-t border-white/8',
        padding ? padding : 'pt-48 pb-28 md:pb-48',
        className
      )}
    >
      {/* Description block */}
      {branding.footer_description && (
        <div className="mb-40">
          <p className="max-w-2xl text-sm leading-7 text-white/40">
            {branding.footer_description}
          </p>
        </div>
      )}

      {/* Menu links + social icons */}
      <Menus />

      {/* Copyright + locale */}
      <div className="mt-20 flex flex-col items-center gap-10 text-center text-white/30 md:flex-row md:justify-between md:text-left">
        <Trans message="Copyright © 2026 World Biz Journals, All Rights Reserved" />
        <LocaleSwitcher />
      </div>
    </footer>
  );
}

function Menus() {
  const settings = useSettings();
  const primaryMenu = settings.menus.find(m => m.positions?.includes('footer'));
  const secondaryMenu = settings.menus.find(m =>
    m.positions?.includes('footer-secondary')
  );

  if (!primaryMenu && !secondaryMenu) return null;

  return (
    <div className="flex flex-col gap-14 border-b border-white/8 pb-20 mb-4 md:flex-row md:items-center md:justify-between md:gap-30">
      {primaryMenu && (
        <CustomMenu menu={primaryMenu} className="text-primary" />
      )}
      {secondaryMenu && (
        <CustomMenu menu={secondaryMenu} className="text-white/40 mt-14 md:mt-0" />
      )}
    </div>
  );
}

function ThemeSwitcher() {
  const {themes} = useSettings();
  const {selectedTheme, selectTheme} = useThemeSelector();
  if (!selectedTheme || !themes?.user_change) return null;

  return (
    <Button
      variant="text"
      startIcon={selectedTheme.is_dark ? <DarkModeIcon /> : <LightbulbIcon />}
      onClick={() => {
        if (selectedTheme.is_dark) {
          selectTheme('light');
        } else {
          selectTheme('dark');
        }
      }}
    >
      {selectedTheme.is_dark ? (
        <Trans message="Dark mode" />
      ) : (
        <Trans message="Light mode" />
      )}
    </Button>
  );
}
